import { useCallback, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from 'utils/pagination';

/**
 * The page and page size a screen is currently showing. The table reads them to
 * render its pager; the screen reads them to ask the API for that slice, which
 * is what keeps the highlighted page number and the rows on screen in step -
 * before, the screen refetched page one on a filter change while antd went on
 * highlighting whichever page the client had been on.
 *
 * `reset` and `onPaginationChange` both return the new value so the caller can
 * dispatch with it straight away rather than waiting for the next render.
 *
 * @param {number} initialPageSize rows per page before the client changes it
 */
const usePagination = (initialPageSize = DEFAULT_PAGE_SIZE) => {
  const [pagination, setPagination] = useState({ current: 1, pageSize: initialPageSize });
  /* Mirrors the state so a filter change can reset to page one without having
     to carry the current page size through its effect's dependencies - which
     would refetch on every page change too. */
  const latest = useRef(pagination);

  const apply = useCallback((next) => {
    latest.current = next;
    setPagination(next);
    return next;
  }, []);

  // A new filter invalidates the page the client was on, but not the size they chose.
  const reset = useCallback(() => apply({ current: 1, pageSize: latest.current.pageSize }), [apply]);

  const onPaginationChange = useCallback(({ current, pageSize }) => apply({ current, pageSize }), [apply]);

  return { pagination, onPaginationChange, reset };
};

export default usePagination;
